import React, { useEffect, useRef, useState } from "react";
import { Play, Square, Users } from "lucide-react";
import { StaffViewer } from "@/components/StaffViewer";
import { AMAZING_GRACE as H, PARTS } from "@/data/hymns";
import { playFreq } from "@/lib/audio";
import { pitchToFreq } from "@/lib/music";

const DUR = { 1: "quarter", 2: "half", 3: "half" };

const SYSTEMS_WIDE = [[0, 7], [7, H.chords.length]];
const SYSTEMS_NARROW = [[0, 3], [3, 7], [7, 11], [11, H.chords.length]];
const partNotes = (idx, from, to) => {
  const out = [];
  H.chords.slice(from, to).forEach((c, k) => {
    const i = from + k;
    if (H.bars.includes(i) && k > 0) out.push({ barline: true });
    out.push({ pitch: c[idx], duration: DUR[c[0]], dot: c[0] === 3, label: c[5] });
  });
  out.push({ barline: to === H.chords.length ? "double" : true });
  return out;
};

export const ChoirPractice = ({ testId = "choir-practice" }) => {
  const [part, setPart] = useState("S");
  const [vols, setVols] = useState({ S: 0.3, A: 0.3, T: 0.3, B: 0.3 });
  const [mine, setMine] = useState(1);
  const [only, setOnly] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [step, setStep] = useState(-1);
  const timers = useRef([]);
  const live = useRef({});
  live.current = { vols, mine, only, part };
  const [narrow, setNarrow] = useState(() => window.innerWidth < 640);
  useEffect(() => { const on = () => setNarrow(window.innerWidth < 640); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on); }, []);
  const SYSTEMS = narrow ? SYSTEMS_NARROW : SYSTEMS_WIDE;
  const P = PARTS.find((p) => p.id === part);

  const stop = () => { timers.current.forEach(clearTimeout); timers.current = []; setPlaying(false); setStep(-1); };
  useEffect(() => stop, []);

  const play = () => {
    stop(); setPlaying(true);
    const beat = 60 / H.bpm;
    let t = 0;
    H.chords.forEach((c, i) => {
      timers.current.push(setTimeout(() => {
        setStep(i);
        const L = live.current;
        PARTS.forEach((p) => {
          const v = p.id === L.part ? L.mine : L.only ? 0 : L.vols[p.id];
          if (v > 0) playFreq(pitchToFreq(c[p.idx]), c[0] * beat * 0.95, v);
        });
      }, t * 1000));
      t += c[0] * beat;
    });
    timers.current.push(setTimeout(stop, t * 1000 + 200));
  };


  return (
    <div className="rounded-2xl border border-primary/30 bg-card p-4 sm:p-6" data-testid={testId}>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Users size={14} /> Choir Part Practice · {H.title}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {PARTS.map((p) => (
          <button key={p.id} onClick={() => { stop(); setPart(p.id); }} data-testid={`${testId}-part-${p.id}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${part === p.id ? "text-[#0B0D12]" : "border border-border text-muted-foreground hover:border-primary"}`}
            style={part === p.id ? { background: p.color } : undefined}>{p.label}</button>
        ))}
      </div>
      <div className="mt-4 rounded-xl border border-border bg-background/60 p-3">
        {SYSTEMS.map(([from, to], si) => {
          const notes = partNotes(P.idx, from, to);
          const idxs = notes.map((n, k) => (n.barline ? null : k)).filter((k) => k !== null);
          const active = step >= from && step < to ? idxs[step - from] : -1;
          return <StaffViewer key={si} clef={P.clef} time={si === 0 ? H.time : undefined} notes={notes} width={narrow ? 340 : 620} showLabels activeIndex={active} testId={`${testId}-staff-${si}`} />;
        })}
        <p className="mt-1 text-center text-xs text-muted-foreground">Your part ({P.label}) — {P.clef} clef. Lyrics under each note.</p>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={playing ? stop : play} data-testid={`${testId}-play`}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
          {playing ? <><Square size={14} /> Stop</> : <><Play size={14} /> Play with the choir</>}
        </button>
        <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
          <input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} data-testid={`${testId}-only`} /> My part only
        </label>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {PARTS.map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`${testId}-vol-${p.id}`}>
            <span className="w-24 font-medium" style={{ color: p.color }}>{p.label}{p.id === part ? " (you)" : ""}</span>
            <input type="range" min="0" max="1" step="0.05" value={p.id === part ? mine : vols[p.id]} disabled={only && p.id !== part}
              onChange={(e) => (p.id === part ? setMine(Number(e.target.value)) : setVols({ ...vols, [p.id]: Number(e.target.value) }))} className="flex-1 accent-[hsl(var(--primary))]" />
          </label>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">Sing your part along with the playback. Turn the other parts down (or tick "My part only") until you're confident, then bring them back up.</p>
    </div>
  );
};
